import { useEffect, useRef, type RefObject } from "react";

/**
 * The focus contract of a panel that opens over the page (drawer, side
 * panel, modal), in one place for both apps.
 *
 * - open: remembers what had the focus (the trigger), moves the focus into
 *   the panel, keeps Tab and Shift+Tab inside it, and Escape calls
 *   `onClose`;
 * - close: gives the focus back to the trigger;
 * - closed but still mounted (a drawer that slides out): the panel is
 *   `inert`, so Tab cannot land on its buttons off screen.
 *
 * QA look v3, 2026-10-26: F04 (AI analysis drawer let Tab out and Escape
 * returned the focus elsewhere), F11 (SKU-list drawer), F14 (report-issue
 * modal), F20 (builder issue drawer) and F25 (closed drawers still
 * tabbable) were the same missing contract in five places.
 */
const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.closest("[inert]") && el.getClientRects().length > 0,
  );
}

export function useDialogFocus(
  open: boolean,
  panelRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  const triggerRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Closed panels that stay mounted are inert (no Tab, no click, hidden
  // from assistive tech). Set on the DOM node: React 18 does not know the
  // attribute.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    if (open) panel.removeAttribute("inert");
    else panel.setAttribute("inert", "");
  }, [open, panelRef]);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    triggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Focus inside after the panel is painted (it may be sliding in).
    const raf = requestAnimationFrame(() => {
      if (!panel.contains(document.activeElement)) {
        const first = focusables(panel)[0];
        if (first) first.focus();
        else {
          if (!panel.hasAttribute("tabindex")) panel.setAttribute("tabindex", "-1");
          panel.focus();
        }
      }
    });
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        if (e.defaultPrevented) return;
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables(panel);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey);
      const trigger = triggerRef.current;
      triggerRef.current = null;
      if (trigger && trigger.isConnected) trigger.focus();
    };
  }, [open, panelRef]);
}
